import type { Tile } from '../hex/types';
import type { BattleEnemyTypeConfig, GameConfig } from '../../config/gameConfig';
import { hexDistance } from '../hex/hexMath';
import type { UnitGroup } from './units';

export type EnemyForce = {
    id: string;
    tileId: string;
    faction: string;
    units: UnitGroup[];
    totalUnits: number;
    createdAt: number;
    defeatedAt?: number;
};

export function spawnEncounters(args: {
    tiles: Tile[];
    types: Record<string, BattleEnemyTypeConfig>;
    chance: number;
    seed: number;
    origin?: { q: number; r: number; s: number };
    scaling?: GameConfig['battle']['encounterScaling'] | null;
}): Record<string, EnemyForce> {
    const rng = createRng(args.seed);
    const landTiles = args.tiles.filter((tile) => tile.biome.type !== 'water');
    const forces: Record<string, EnemyForce> = {};
    let counter = 0;

    landTiles.forEach((tile) => {
        const scaledChance = getScaledChance(args, tile, rng);
        if (rng() >= scaledChance) return;
        const enemy = pickEnemyType(args.types, rng);
        if (!enemy) return;
        const count = getScaledCount(args, tile, enemy.countRange, rng);
        if (count <= 0) return;
        counter += 1;
        forces[tile.id] = {
            id: `enemy_${counter}`,
            tileId: tile.id,
            faction: enemy.id,
            units: [{ type: enemy.id, count }],
            totalUnits: count,
            createdAt: 0,
        };
    });

    return forces;
}

function getScaledChance(
    args: {
        chance: number;
        origin?: { q: number; r: number; s: number };
        scaling?: GameConfig['battle']['encounterScaling'] | null;
    },
    tile: Tile,
    rng: () => number,
): number {
    if (!args.scaling || !args.origin) return clamp01(args.chance);
    const distance = hexDistance(args.origin, tile.position);
    const t = clamp01(distance / Math.max(1, args.scaling.maxDistance));
    const base = lerp(args.scaling.minChance, args.scaling.maxChance, t);
    const jitter = lerp(1 - args.scaling.countJitter, 1 + args.scaling.countJitter, rng());
    return clamp01(base * jitter);
}

function getScaledCount(
    args: {
        origin?: { q: number; r: number; s: number };
        scaling?: GameConfig['battle']['encounterScaling'] | null;
    },
    tile: Tile,
    range: [number, number],
    rng: () => number,
): number {
    const baseCount = randInt(range[0], range[1], rng);
    if (!args.scaling || !args.origin) return baseCount;
    const distance = hexDistance(args.origin, tile.position);
    const t = clamp01(distance / Math.max(1, args.scaling.maxDistance));
    const scale = lerp(args.scaling.minCountMultiplier, args.scaling.maxCountMultiplier, t);
    const jitter = lerp(1 - args.scaling.countJitter, 1 + args.scaling.countJitter, rng());
    return Math.max(1, Math.round(baseCount * scale * jitter));
}

function pickEnemyType(
    types: Record<string, BattleEnemyTypeConfig>,
    rng: () => number,
): BattleEnemyTypeConfig | null {
    const entries = Object.values(types);
    const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
    if (total <= 0) return null;
    let roll = rng() * total;
    for (const entry of entries) {
        if (roll < entry.weight) return entry;
        roll -= entry.weight;
    }
    return entries[entries.length - 1] || null;
}

function randInt(min: number, max: number, rng: () => number): number {
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return Math.floor(low + (high - low + 1) * rng());
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function clamp01(value: number): number {
    return Math.max(0, Math.min(1, value));
}

function createRng(seed: number): () => number {
    let t = seed >>> 0;
    return () => {
        t += 0x6d2b79f5;
        let x = Math.imul(t ^ (t >>> 15), 1 | t);
        x ^= x + Math.imul(x ^ (x >>> 7), 61 | x);
        return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
}
